import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { DailyNavExpectedAllocation } from 'api/models/DailyNavExpectedAllocation';
import { FormDialog } from 'components';
import {
  DAILY_NAV_REPORT_PHASES,
  DailyNavReportTab,
  isValidStaffFinalizeMessage,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { captureException } from 'scalarAnalytics/postHog';
import { useFirmDocumentActions } from 'services/hooks/documents';
import { useFinalizeDailyNavDay } from 'services/hooks/firms';
import {
  useGenerateDailyNavReport,
  useGetDailyNavReport,
  useGetDailyNavReportSource,
  useGetDailyNavValidation,
  useSendDailyNavDay,
  useUnfinalizeDailyNavDay,
} from 'services/hooks/firms/useDailyNavReport';
import { Logger } from 'utilities';
import FinalizeTab from './FinalizeTab';
import PreviewTab from './PreviewTab';
import { ReportDialogProps } from './ReportDialog.props';
import { reportDialogColors as colors, reportDialogTabSx } from './ReportDialog.styles';
import SendTab from './SendTab';
import ValidateTab from './ValidateTab';

const logReportGenerateError = (error: unknown, firmId?: number, date?: string) => {
  Logger.error('Error generating daily nav report', error);
  captureException(error, {
    source: 'daily_nav_report_generate',
    firm_id: firmId,
    date,
  });
};

const ReportDialog: FC<ReportDialogProps> = ({ open, onClose, firmId, date, day, initialTab, isStaff }) => {
  const [tab, setTab] = useState<DailyNavReportTab>('validate');
  const [approverNote, setApproverNote] = useState('');
  const [unfinalizeReason, setUnfinalizeReason] = useState('');
  // What validate said this day would close against, captured when the operator
  // moved past it. Null until then, which is why Finalize refuses.
  const [approvedAllocations, setApprovedAllocations] = useState<DailyNavExpectedAllocation[] | null>(null);
  const previewGenerationRequested = useRef(false);

  const {
    data: validation,
    isFetching: isValidating,
    refetch: refetchValidation,
  } = useGetDailyNavValidation(firmId, date, open);
  const { data: report } = useGetDailyNavReport(firmId, date, open);
  const { data: reportSource, isFetching: isFetchingSource } = useGetDailyNavReportSource(firmId, date, open);
  const {
    data: generatedReport,
    mutateAsync: generateDailyNavReport,
    isPending: isGeneratingReport,
  } = useGenerateDailyNavReport(firmId);
  const { mutateAsync: finalizeDailyNavDay, isPending: isFinalizing } = useFinalizeDailyNavDay(firmId);
  const { mutateAsync: unfinalizeDailyNavDay, isPending: isUnfinalizing } = useUnfinalizeDailyNavDay(firmId);
  const { mutateAsync: sendDailyNavDay, isPending: isSending } = useSendDailyNavDay(firmId);
  const { downloadDocuments, isDownloadingDocuments } = useFirmDocumentActions();

  const isFinalized = Boolean(day?.is_finalized);
  const isSent = Boolean(day?.is_sent);
  const validationPassed = Boolean(validation?.passed);
  const displayedReport = generatedReport?.date === date ? generatedReport : report;
  const hasGeneratedReport = Boolean(displayedReport?.report_file_id);

  const canPreview = validationPassed || isFinalized || isSent;
  const canSendTab = isFinalized || isSent;

  useEffect(() => {
    if (!open) {
      setApproverNote('');
      setUnfinalizeReason('');
      previewGenerationRequested.current = false;
      return;
    }
    if (initialTab) {
      setTab(initialTab);
      return;
    }
    setApprovedAllocations(null);
    setTab(isSent || isFinalized ? 'send' : 'validate');
  }, [open, initialTab, isFinalized, isSent]);

  useEffect(() => {
    if (
      open &&
      tab === 'preview' &&
      validationPassed &&
      !isFinalized &&
      !isSent &&
      !previewGenerationRequested.current
    ) {
      previewGenerationRequested.current = true;
      generateDailyNavReport({ date }).catch(error => {
        logReportGenerateError(error, firmId, date);
        previewGenerationRequested.current = false;
      });
    }
  }, [open, tab, validationPassed, isFinalized, isSent, generateDailyNavReport, date, firmId]);

  const sourceRows = useMemo(() => reportSource?.rows ?? [], [reportSource?.rows]);
  const artifactColumns = useMemo(() => displayedReport?.columns ?? [], [displayedReport?.columns]);
  const artifactRows = useMemo(() => displayedReport?.rows ?? [], [displayedReport?.rows]);
  const recipients = day?.recipients?.length ? day.recipients : [];

  const canConfirmFinalize =
    validationPassed && hasGeneratedReport && !isFinalized && (!isStaff || isValidStaffFinalizeMessage(approverNote));

  const handleFinalize = async () => {
    if (!canConfirmFinalize) {
      return;
    }
    // The snapshot taken when the operator left the validate tab, NOT the live
    // query: the server refuses if its own resolution differs, so the day
    // commits what was reviewed. Never rebuild this client-side.
    if (!approvedAllocations) {
      enqueueSnackbar('Re-run validation before finalizing.', { variant: 'warning' });
      return;
    }
    await finalizeDailyNavDay({
      date,
      finalize_approver_note: isStaff ? approverNote.trim() : undefined,
      expected_allocations: approvedAllocations,
    });
    setTab('send');
  };

  const handleUnfinalize = async () => {
    if (!unfinalizeReason.trim()) {
      return;
    }
    await unfinalizeDailyNavDay({ date, reason: unfinalizeReason.trim() });
    setTab('validate');
  };

  const handleSend = async () => {
    await sendDailyNavDay({ date });
  };

  const handleTabChange = async (_event: SyntheticEvent, value: DailyNavReportTab) => {
    previewGenerationRequested.current = value === 'preview';
    // Freeze the allocations the operator is moving forward with. The validate
    // query has staleTime 0 and refetches on window focus, so reading it live
    // at click time would let an alt-tab silently replace the token with the
    // server's CURRENT answer -- defeating the guard it exists to be.
    if (tab === 'validate' && value !== 'validate' && validation?.expected_allocations) {
      setApprovedAllocations(validation.expected_allocations);
    }
    setTab(value);
    if (value === 'preview' && validationPassed && !isFinalized && !isSent) {
      try {
        await generateDailyNavReport({ date });
      } catch (error) {
        logReportGenerateError(error, firmId, date);
      }
    }
  };

  const canDownloadReport = Boolean(firmId && displayedReport?.report_file_id);
  const handleDownload = () => {
    if (!firmId || !displayedReport?.report_file_id) {
      return;
    }
    downloadDocuments({
      documents: [
        {
          fileId: displayedReport.report_file_id,
          fileName: displayedReport.filename || `daily-nav-${date ?? 'report'}.csv`,
          firmId,
        },
      ],
    }).catch(() => undefined);
  };

  const isTabEnabled = (phaseTab: DailyNavReportTab) => {
    if (phaseTab === 'validate') {
      return true;
    }
    if (phaseTab === 'send') {
      return canSendTab;
    }
    if (phaseTab === 'finalize') {
      return (hasGeneratedReport && validationPassed) || isFinalized || isSent;
    }
    return canPreview;
  };

  let footerButton;
  if (tab === 'validate') {
    footerButton = (
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => refetchValidation()}
        disabled={isValidating}
        data-testid="daily-nav-rerun-validations"
        sx={{
          borderColor: colors.border,
          color: colors.ink,
          textTransform: 'none',
        }}>
        Re-run validations
      </Button>
    );
  } else if (tab === 'preview' && !isFinalized && !isSent) {
    footerButton = (
      <Button
        variant="outlined"
        color="inherit"
        onClick={() =>
          generateDailyNavReport({ date }).catch(error => {
            logReportGenerateError(error, firmId, date);
          })
        }
        disabled={!validationPassed || isGeneratingReport}
        data-testid="daily-nav-regenerate-report"
        sx={{ borderColor: colors.border, color: colors.ink, textTransform: 'none' }}>
        Regenerate preview
      </Button>
    );
  }

  return (
    <FormDialog
      title="Prepare Report"
      open={open}
      editMode
      isValid
      isLoading={
        isFinalizing ||
        isSending ||
        isUnfinalizing ||
        isGeneratingReport ||
        (tab === 'validate' && (isValidating || isFetchingSource))
      }
      showSaveButton={false}
      showCancelButton={false}
      disableTitleClose={false}
      bigDialog
      onClose={onClose}
      customButton={footerButton}>
      <Box>
        <Tabs value={tab} onChange={handleTabChange} sx={reportDialogTabSx} data-testid="daily-nav-report-tabs">
          {DAILY_NAV_REPORT_PHASES.map(phase => (
            <Tab
              key={phase.tab}
              value={phase.tab}
              label={phase.label}
              disabled={!isTabEnabled(phase.tab)}
              data-testid={`daily-nav-report-tab-${phase.tab}`}
            />
          ))}
        </Tabs>

        {tab === 'validate' && <ValidateTab checks={validation?.checks} sourceRows={sourceRows} />}

        {tab === 'preview' && (
          <PreviewTab hasGeneratedReport={hasGeneratedReport} columns={artifactColumns} rows={artifactRows} />
        )}

        {tab === 'finalize' && (
          <FinalizeTab
            columns={artifactColumns}
            rows={artifactRows}
            day={day}
            isFinalized={isFinalized}
            isSent={isSent}
            isStaff={isStaff}
            validationPassed={validationPassed}
            hasGeneratedReport={hasGeneratedReport}
            approverNote={approverNote}
            onApproverNoteChange={setApproverNote}
            unfinalizeReason={unfinalizeReason}
            onUnfinalizeReasonChange={setUnfinalizeReason}
            canConfirmFinalize={canConfirmFinalize}
            isFinalizing={isFinalizing}
            isUnfinalizing={isUnfinalizing}
            onFinalize={handleFinalize}
            onUnfinalize={handleUnfinalize}
            canDownloadReport={canDownloadReport}
            isDownloadingDocuments={isDownloadingDocuments}
            onDownload={handleDownload}
          />
        )}

        {tab === 'send' && (
          <SendTab
            columns={artifactColumns}
            rows={artifactRows}
            day={day}
            recipients={recipients}
            isSent={isSent}
            isFinalized={isFinalized}
            isSending={isSending}
            onSend={handleSend}
            canDownloadReport={canDownloadReport}
            isDownloadingDocuments={isDownloadingDocuments}
            onDownload={handleDownload}
          />
        )}
      </Box>
    </FormDialog>
  );
};

export default ReportDialog;
